"use client";

import {
	Copy01Icon,
	CopyCheckIcon,
	QrCode01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { forwardRef, useEffect, useState } from "react";
import Logo from "@/components/brand/Logo";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyTitle,
} from "@/components/ui/empty";
import { UserAvatar } from "@/components/ui/user-avatar";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HandleCardProps = {
	handle?: string | null;
	name?: string | null;
	image?: string | null;
	roleLabel?: string | null;
	/** Full URL to encode in the QR code (e.g. profile URL with verification token). Falls back to raw handle. */
	qrValue?: string | null;
	className?: string;
};

type CopyState = "idle" | "copied" | "error";

// ---------------------------------------------------------------------------
// Clipboard Helper
// ---------------------------------------------------------------------------

async function copyText(value: string) {
	if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(value);
		return;
	}

	if (typeof document === "undefined") {
		throw new Error("Clipboard is unavailable");
	}

	const textarea = document.createElement("textarea");
	textarea.value = value;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "absolute";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.select();
	const copied = document.execCommand("copy");
	document.body.removeChild(textarea);

	if (!copied) {
		throw new Error("Clipboard copy failed");
	}
}

// ---------------------------------------------------------------------------
// HandleCard Component
// ---------------------------------------------------------------------------

export const HandleCard = forwardRef<HTMLDivElement, HandleCardProps>(
	function HandleCard(
		{ handle, name, image, roleLabel, qrValue, className },
		ref,
	) {
		const [copyState, setCopyState] = useState<CopyState>("idle");

		useEffect(() => {
			if (copyState === "idle") return;
			const timeout = window.setTimeout(() => setCopyState("idle"), 1800);
			return () => window.clearTimeout(timeout);
		}, [copyState]);

		const handleCopy = async () => {
			if (!handle) return;
			try {
				await copyText(handle);
				setCopyState("copied");
			} catch (error) {
				console.error(error);
				setCopyState("error");
			}
		};

		// -----------------------------------------------------------------------
		// Empty state
		// -----------------------------------------------------------------------

		if (!handle) {
			return (
				<Empty
					className={cn(
						"rounded-[28px] border-border/70 bg-muted/20 p-5",
						className,
					)}
				>
					<EmptyHeader>
						<EmptyTitle className="text-muted-foreground text-xs uppercase tracking-[0.18em]">
							Handle
						</EmptyTitle>
						<EmptyDescription>
							Your public handle will appear here once your account is fully
							ready.
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			);
		}

		// -----------------------------------------------------------------------
		// Player Card
		// -----------------------------------------------------------------------

		const displayRole = roleLabel?.trim() || "Player";

		return (
			<div
				ref={ref}
				className={cn(
					"mx-auto flex w-full max-w-sm flex-col overflow-hidden rounded-3xl border border-border/70 bg-card dark:bg-muted/20",
					className,
				)}
			>
				{/* Header — Brand strip */}
				<div className="flex items-center justify-between border-border/50 border-b bg-muted/40 px-6 py-4 dark:bg-muted/15">
					<Logo variant="combo" size="sm" className="text-foreground/90" />
					<span className="font-bold text-[10px] text-muted-foreground/80 uppercase tracking-[0.2em]">
						{displayRole}
					</span>
				</div>

				{/* Body — Player info + QR */}
				<div className="flex flex-col items-center gap-8 px-6 pt-8 pb-6">
					{/* Avatar + Name */}
					<div className="flex flex-col items-center gap-4">
						<UserAvatar
							className="size-22 border-[3px] border-background shadow-sm"
							image={image}
							name={name}
							seed={handle ?? ""}
						/>
						{name && (
							<h3 className="text-center font-bold text-2xl text-foreground tracking-tight">
								{name}
							</h3>
						)}
					</div>

					{/* QR Code */}
					<div className="flex flex-col items-center gap-4 rounded-2xl border border-border/60 bg-muted/25 p-4">
						<div className="relative flex size-44 items-center justify-center overflow-hidden rounded-xl bg-white">
							<motion.div
								initial={{ opacity: 0, scale: 0.96 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.4, ease: "easeOut" }}
								className="flex size-full items-center justify-center rounded-xl border border-border/50 bg-white will-change-transform"
							>
								<QRCodeSVG
									value={qrValue || handle}
									size={150}
									bgColor="#00000000"
									fgColor="#111111"
									level="M"
									marginSize={1}
									title={`QR code for handle ${handle}`}
								/>
							</motion.div>
						</div>
						<div className="flex items-center gap-1.5 font-bold text-[10px] text-muted-foreground/60 uppercase tracking-[0.2em]">
							<HugeiconsIcon icon={QrCode01Icon} className="size-3" />
							Scan to capture
						</div>
					</div>
				</div>

				{/* Footer — Handle + Copy */}
				<div className="border-border/70 border-t">
					<button
						type="button"
						onClick={handleCopy}
						className="group relative flex w-full items-center justify-center px-5 py-4 text-center transition-colors duration-200 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
						aria-label={`Copy handle ${handle}`}
					>
						<div className="relative flex h-7 items-center justify-center">
							<AnimatePresence mode="wait" initial={false}>
								{copyState === "copied" ? (
									<motion.span
										key="copied"
										initial={{ opacity: 0, y: 15 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -15 }}
										transition={{ duration: 0.2, ease: "easeOut" }}
										className="block font-bold font-mono text-lg text-primary tracking-[0.15em] sm:text-xl"
									>
										COPIED
									</motion.span>
								) : copyState === "error" ? (
									<motion.span
										key="error"
										initial={{ opacity: 0, y: 15 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -15 }}
										transition={{ duration: 0.2, ease: "easeOut" }}
										className="block font-bold font-mono text-destructive text-lg tracking-[0.15em] sm:text-xl"
									>
										FAILED
									</motion.span>
								) : (
									<motion.span
										key="handle"
										initial={{ opacity: 0, y: 15 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -15 }}
										transition={{ duration: 0.2, ease: "easeOut" }}
										className="block break-all font-mono font-semibold text-base text-foreground tracking-[0.12em] sm:text-lg"
									>
										{handle}
									</motion.span>
								)}
							</AnimatePresence>
						</div>
					</button>

					{/* Hidden from download via data-capture-exclude */}
					<button
						type="button"
						onClick={handleCopy}
						data-capture-exclude
						className={cn(
							"flex w-full items-center justify-center gap-2 border-border/70 border-t px-4 py-3.5 font-medium text-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
							copyState === "copied"
								? "bg-secondary text-secondary-foreground hover:bg-secondary/80"
								: "bg-muted/35 text-foreground/80 hover:bg-muted/55",
						)}
					>
						<HugeiconsIcon
							icon={copyState === "copied" ? CopyCheckIcon : Copy01Icon}
							className="size-4"
						/>
						{copyState === "copied"
							? "Copied"
							: copyState === "error"
								? "Try again"
								: "Copy"}
					</button>
				</div>
			</div>
		);
	},
);
